/**
 * Tabella rappresentanti commerciali di un fornitore.
 *
 * Usata nella pagina FornitoreDetail come contenuto del tab "Rappresentanti".
 * Include form inline per aggiunta rapida, modifica inline e soft delete.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Pencil, Trash2, X, Check } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

interface RappresentanteItem {
  id: string;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  telefono: string | null;
  zona: string | null;
  ruolo: string;
  priorita: number;
  stato: string;
}

interface RappresentantiTableProps {
  /** UUID del soggetto fornitore. */
  fornitoreId: string;
}

/** Headers comuni per le fetch autenticate. */
const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

/**
 * Tabella rappresentanti con aggiunta rapida, modifica inline e soft delete.
 */
const RappresentantiTable: React.FC<RappresentantiTableProps> = ({
  fornitoreId,
}: RappresentantiTableProps) => {
  const [items, setItems] = useState<RappresentanteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form aggiunta rapida
  const [showForm, setShowForm] = useState(false);
  const [newNome, setNewNome] = useState('');
  const [newCognome, setNewCognome] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newTelefono, setNewTelefono] = useState('');
  const [newRuolo, setNewRuolo] = useState('agente');
  const [newZona, setNewZona] = useState('');
  const [saving, setSaving] = useState(false);

  // Modifica inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState('');
  const [editCognome, setEditCognome] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editTelefono, setEditTelefono] = useState('');
  const [editRuolo, setEditRuolo] = useState('');
  const [editZona, setEditZona] = useState('');

  const baseUrl = `/api/v1/fornitori/${fornitoreId}/rappresentanti`;

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(baseUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: RappresentanteItem[] = await response.json();
      setItems(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    void carica();
  }, [carica]);

  /** Crea un nuovo rappresentante. */
  const handleAdd = async () => {
    if (!newRuolo.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        nome: newNome.trim() || null,
        cognome: newCognome.trim() || null,
        email: newEmail.trim() || null,
        telefono: newTelefono.trim() || null,
        ruolo: newRuolo.trim(),
        zona: newZona.trim() || null,
      };
      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setNewNome('');
      setNewCognome('');
      setNewEmail('');
      setNewTelefono('');
      setNewRuolo('agente');
      setNewZona('');
      setShowForm(false);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  /** Entra in modalita modifica su una riga. */
  const startEdit = (item: RappresentanteItem) => {
    setEditingId(item.id);
    setEditNome(item.nome ?? '');
    setEditCognome(item.cognome ?? '');
    setEditEmail(item.email ?? '');
    setEditTelefono(item.telefono ?? '');
    setEditRuolo(item.ruolo);
    setEditZona(item.zona ?? '');
  };

  /** Salva la modifica inline con PATCH. */
  const handleSaveEdit = async () => {
    if (!editingId || !editRuolo.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        nome: editNome.trim() || null,
        cognome: editCognome.trim() || null,
        email: editEmail.trim() || null,
        telefono: editTelefono.trim() || null,
        ruolo: editRuolo.trim(),
        zona: editZona.trim() || null,
      };
      const response = await fetch(`${baseUrl}/${editingId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setEditingId(null);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  /** Soft delete di un rappresentante. */
  const handleDelete = async (itemId: string) => {
    try {
      const response = await fetch(`${baseUrl}/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        {loading && (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Caricamento…
          </p>
        )}
        {error && (
          <p className="p-6 text-center text-sm text-destructive">{error}</p>
        )}
        {!loading && !error && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Zona</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead className="text-center">Priorità</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && !showForm && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessun rappresentante
                    </TableCell>
                  </TableRow>
                )}
                {items.map((item: RappresentanteItem) =>
                  editingId === item.id ? (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex gap-1">
                          <Input
                            placeholder="Nome"
                            value={editNome}
                            onChange={(e) => setEditNome(e.target.value)}
                            className="h-8 w-20"
                          />
                          <Input
                            placeholder="Cognome"
                            value={editCognome}
                            onChange={(e) => setEditCognome(e.target.value)}
                            className="h-8 w-24"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editRuolo}
                          onChange={(e) => setEditRuolo(e.target.value)}
                          className="h-8 w-24"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editZona}
                          onChange={(e) => setEditZona(e.target.value)}
                          className="h-8 w-28"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          className="h-8 w-36"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editTelefono}
                          onChange={(e) => setEditTelefono(e.target.value)}
                          className="h-8 w-28"
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        {item.priorita}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            item.stato === 'attivo' ? 'default' : 'secondary'
                          }
                        >
                          {item.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={handleSaveEdit}
                            disabled={saving}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {[item.cognome, item.nome].filter(Boolean).join(' ') ||
                          '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.ruolo}</Badge>
                      </TableCell>
                      <TableCell>{item.zona ?? '—'}</TableCell>
                      <TableCell className="text-sm">
                        {item.email ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.telefono ?? '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.priorita}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            item.stato === 'attivo' ? 'default' : 'secondary'
                          }
                        >
                          {item.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => startEdit(item)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>

            {/* Form aggiunta rapida */}
            {showForm && (
              <div className="flex flex-wrap items-end gap-3 border-t p-4">
                <Input
                  placeholder="Nome"
                  value={newNome}
                  onChange={(e) => setNewNome(e.target.value)}
                  className="w-28"
                />
                <Input
                  placeholder="Cognome"
                  value={newCognome}
                  onChange={(e) => setNewCognome(e.target.value)}
                  className="w-28"
                />
                <Input
                  placeholder="Ruolo"
                  value={newRuolo}
                  onChange={(e) => setNewRuolo(e.target.value)}
                  className="w-28"
                />
                <Input
                  placeholder="Zona"
                  value={newZona}
                  onChange={(e) => setNewZona(e.target.value)}
                  className="w-28"
                />
                <Input
                  placeholder="Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-40"
                />
                <Input
                  placeholder="Telefono"
                  value={newTelefono}
                  onChange={(e) => setNewTelefono(e.target.value)}
                  className="w-32"
                />
                <Button size="sm" onClick={handleAdd} disabled={saving}>
                  {saving ? 'Salva…' : 'Aggiungi'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowForm(false)}
                >
                  Annulla
                </Button>
              </div>
            )}

            {!showForm && (
              <div className="border-t p-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowForm(true)}
                >
                  Aggiungi rappresentante
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RappresentantiTable;
